/**
 * Hover-to-identify overlay (TEC-465 P3). A reviewer toggles inspector mode on
 * the live site; declaratively-rendered messages (wrapped by <Msg>) get a
 * dashed outline, and hovering one opens a popover with its message id, the
 * English original and copy buttons (inspector-popover.ts, shared with the two
 * static surfaces). There is no in-place editor: this overlay is look-up only.
 *
 * Renders nothing when inspector mode is not available (non-reviewers can
 * never reach it, even if the flag is forced). Mount once under IntlProvider.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useI18nInspector } from './inspector';
import { installInspectorPopover } from './inspector-popover';
import { INSPECTOR_KEYS_HINT } from './inspector-keys-hint';

/** Shared with StaticInspectorMount.tsx (non-hydrating routes) so the outline/flash rules and
 * class names stay single-sourced across both mount paths. */
export const OVERLAY_STYLE = `
.i18n-inspect-on [data-i18n-inspectable] {
  outline: 1px dashed color-mix(in srgb, var(--jc-accent) 70%, transparent);
  outline-offset: 1px;
  cursor: help;
}
.i18n-inspect-on [data-i18n-inspectable]:hover {
  outline: 2px solid var(--jc-accent);
  background: color-mix(in srgb, var(--jc-accent) 8%, transparent);
}
.i18n-inspect-on [data-i18n-showing-english] {
  box-shadow: inset 0 -2px 0 var(--jc-accent);
}
.i18n-inspect-flash {
  animation: i18n-inspect-flash 1.6s ease-out;
}
@keyframes i18n-inspect-flash {
  0% { background: color-mix(in srgb, var(--jc-accent) 35%, transparent); }
  100% { background: transparent; }
}
`;


/** What a host's floating panel gets to render; see adapters/InspectorPanel.tsx for jobcrawls'. */
export interface InspectorPanelProps {
  enabled: boolean;
  setEnabled: (on: boolean) => void;
  /** Hidden on English pages: there is nothing to swap. */
  canShowEnglish: boolean;
  showEnglish: boolean;
  setShowEnglish: (on: boolean) => void;
  lastCopied: string | null;
}

export function InspectorOverlay({
  Panel = PlainInspectorPanel,
}: {
  Panel?: React.ComponentType<InspectorPanelProps>;
} = {}): React.ReactElement | null {
  const { available, enabled, setEnabled, focusId, setFocusId, showEnglish, setShowEnglish, englishMessages } =
    useI18nInspector();
  const { locale } = useIntl();
  const [lastCopied, setLastCopied] = useState<string | null>(null);

  // Toggle a body class so the marker outline CSS applies only while enabled.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('i18n-inspect-on', enabled);
    return () => document.body.classList.remove('i18n-inspect-on');
  }, [enabled]);

  // The popover reads these through refs so it isn't reinstalled on every toggle or catalogue load.
  const englishRef = useRef(englishMessages);
  englishRef.current = englishMessages;
  const showEnglishRef = useRef(showEnglish);
  showEnglishRef.current = showEnglish;

  useEffect(() => {
    if (!enabled || typeof document === 'undefined') return;
    return installInspectorPopover({
      selector: '[data-i18n-inspectable]',
      idLabel: 'Id',
      getId: (el) => el.getAttribute('data-i18n-id') ?? '',
      getEnglish: (el) => {
        const catalogue = englishRef.current;
        if (!catalogue) return undefined;
        return catalogue[el.getAttribute('data-i18n-id') ?? ''] ?? null;
      },
      locale,
      isEnglishOn: () => showEnglishRef.current,
      toggleEnglish: () => setShowEnglish(!showEnglishRef.current),
      onCopy: setLastCopied,
    }).dispose;
  }, [enabled, locale, setShowEnglish]);

  // Deep-link focus: scroll to and briefly flash ?i18nInspect=<id>, the id's
  // popover is available from there same as any other marked string.
  useEffect(() => {
    if (!enabled || !focusId) return;
    const marker = document.querySelector(`[data-i18n-id="${CSS.escape(focusId)}"]`) as HTMLElement | null;
    if (marker) {
      marker.scrollIntoView({ behavior: 'smooth', block: 'center' });
      marker.classList.add('i18n-inspect-flash');
      setTimeout(() => marker.classList.remove('i18n-inspect-flash'), 1600);
    }
    setFocusId(null);
  }, [enabled, focusId, setFocusId]);

  if (!available) return null;

  return (
    <>
      <style>{OVERLAY_STYLE}</style>
      <Panel
        enabled={enabled}
        setEnabled={setEnabled}
        canShowEnglish={locale !== 'en'}
        showEnglish={showEnglish}
        setShowEnglish={setShowEnglish}
        lastCopied={lastCopied}
      />
    </>
  );
}

/** Unstyled default for hosts without their own design system. */
function PlainInspectorPanel(props: InspectorPanelProps): React.ReactElement {
  return (
    <div
      data-i18n-inspector-ui=""
      style={{ position: 'fixed', bottom: 16, right: 16, zIndex: 2147483000, maxWidth: 320, padding: 8, background: '#fff', border: '1px solid #ccc', font: '12px system-ui' }}
    >
      <label style={{ display: 'block' }}>
        <input type="checkbox" checked={props.enabled} onChange={(e) => props.setEnabled(e.target.checked)} /> Inspect
        translations
      </label>
      {props.enabled && props.canShowEnglish && (
        <label style={{ display: 'block' }}>
          <input type="checkbox" checked={props.showEnglish} onChange={(e) => props.setShowEnglish(e.target.checked)} />{' '}
          Show English
        </label>
      )}
      {props.enabled && (
        <>
          <p>{INSPECTOR_KEYS_HINT}</p>
          <p>Last copied: {props.lastCopied ?? 'nothing yet'}</p>
        </>
      )}
    </div>
  );
}
